import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const ROWS = [
  ["client", "Promemoria ai clienti", "WhatsApp il giorno prima dell'appuntamento"],
  ["tech", "Agenda di domani ai tecnici", "Notifica con gli interventi del giorno dopo"],
  ["punch", "Timbra l'uscita", "Ai dipendenti che non hanno ancora timbrato l'uscita"],
  ["report", "Rapportino cantiere mancante", "La mattina dopo, al responsabile e all'admin"],
];

const TimeSel = ({ k, r, set }) => (
  <select value={r[k]} onChange={(e) => set(k, e.target.value)} className="rounded-lg border bg-white px-2 py-1.5 font-mono text-sm" data-testid={`reminder-${k.replace("_", "-")}`}>
    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
  </select>
);
const plus15 = (t) => { const m = Number(t.slice(0, 2)) * 60 + Number(t.slice(3)) + 15; return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; };

function WorkHours({ r, set }) {
  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="reminder-work-hours">
      <p className="text-sm font-medium">Orario di lavoro</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>dalle</span><TimeSel k="work_start" r={r} set={set} /><span>pausa</span><TimeSel k="lunch_start" r={r} set={set} />
        <span>–</span><TimeSel k="lunch_end" r={r} set={set} /><span>alle</span><TimeSel k="work_end" r={r} set={set} />
      </div>
      <p className="text-xs text-slate-500">I messaggi WhatsApp ai clienti partono solo in questo orario e nei giorni lavorativi: se l'ora scelta cade fuori, il messaggio aspetta l'inizio dell'orario di lavoro.</p>
    </div>
  );
}

function Row({ k, label, desc, r, set }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`reminder-row-${k}`}>
      <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-slate-500">{desc}</p></div>
      <div className="flex items-center gap-3">
        <select value={r[`${k}_time`]} disabled={!r[`${k}_enabled`]} onChange={(e) => set(`${k}_time`, e.target.value)}
          className="rounded-lg border bg-white px-2 py-1.5 font-mono text-sm disabled:opacity-50" data-testid={`reminder-${k}-time`}>
          {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Switch checked={r[`${k}_enabled`]} onCheckedChange={(v) => set(`${k}_enabled`, v)} data-testid={`reminder-${k}-switch`} />
      </div>
    </div>
  );
}

export function ReminderSettingsCard() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings/reminders").then((res) => setR(res.data)); }, []);
  if (!r) return null;
  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      setR((await api.put("/admin/settings/reminders", { ...r, ack_hours: Number(r.ack_hours) || 2 })).data);
      toast.success("Promemoria salvati");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-2xl border bg-white p-5 sm:p-6" data-testid="reminder-settings-card">
      <h2 className="font-display text-lg font-semibold">Promemoria automatici</h2>
      <p className="mb-2 text-xs text-slate-500">Orari in ora italiana, a passi di 15 minuti. I messaggi al cliente partono solo dopo che il tecnico esterno ha accettato.</p>
      <div className="divide-y">
        <WorkHours r={r} set={set} />
        {ROWS.map(([k, label, desc]) => <Row key={k} k={k} label={label} desc={desc} r={r} set={set} />).slice(0, 1)}
        <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid="reminder-row-client2">
          <div><p className="text-sm font-medium">Secondo promemoria ai clienti</p><p className="text-xs text-slate-500">WhatsApp poco prima dell'appuntamento, lo stesso giorno</p></div>
          <div className="flex items-center gap-3">
            <select value={r.client2_minutes} disabled={!r.client2_enabled} onChange={(e) => set("client2_minutes", Number(e.target.value))}
              className="rounded-lg border bg-white px-2 py-1.5 text-sm disabled:opacity-50" data-testid="reminder-client2-minutes">
              {[30, 60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min prima` : `${m / 60} ${m === 60 ? "ora" : "ore"} prima`}</option>)}
            </select>
            <Switch checked={r.client2_enabled} onCheckedChange={(v) => set("client2_enabled", v)} data-testid="reminder-client2-switch" />
          </div>
        </div>
        {ROWS.map(([k, label, desc]) => <Row key={k} k={k} label={label} desc={desc} r={r} set={set} />).slice(1, 2)}
        <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid="reminder-row-tech-soon">
          <div><p className="text-sm font-medium">Interventi del giorno al tecnico</p><p className="text-xs text-slate-500" data-testid="reminder-tech-soon-desc">Alle {plus15(r.work_start)} quelli della mattina, alle {r.lunch_end} (fine pausa) quelli del pomeriggio</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <Switch checked={r.tech_soon_enabled} onCheckedChange={(v) => set("tech_soon_enabled", v)} data-testid="reminder-tech-soon-switch" />
          </div>
        </div>
        {ROWS.map(([k, label, desc]) => <Row key={k} k={k} label={label} desc={desc} r={r} set={set} />).slice(2)}
        <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid="reminder-row-ack">
          <div><p className="text-sm font-medium">Intervento non accettato</p><p className="text-xs text-slate-500">Avviso all'admin se un autonomo non accetta entro</p></div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5"><Input type="number" min={1} max={48} value={r.ack_hours} disabled={!r.ack_enabled}
              onChange={(e) => set("ack_hours", e.target.value)} className="w-20" data-testid="reminder-ack-hours" /><span className="text-sm">ore</span></div>
            <Switch checked={r.ack_enabled} onCheckedChange={(v) => set("ack_enabled", v)} data-testid="reminder-ack-switch" />
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 rounded-full" data-testid="reminder-save-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva promemoria
      </Button>
    </section>
  );
}
